import React, { createContext, useContext, useEffect, useId, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Divider, FormControlLabel, FormGroup, Grid, IconButton, Switch } from '@mui/material';
import TextField from '@mui/material/TextField';
import useApi from '../../../shared/hooks/useApi';
import CloseIcon from '@mui/icons-material/Close';
import { useSettings } from '../../context/SettingsContext';
import { Tabs, Tab } from '@mui/material';

const SettingsModal = ({ open, onClose, setLoading, triggerAlert }) => {
    const [settings, setSettings] = useState({});
    const [originalSettings, setOriginalSettings] = useState({});
    const { apiPost, apiGet, loadingApi } = useApi();
    const { settingsPairs, setSettingsPairs } = useSettings();
    const [currentTab, setCurrentTab] = useState(0);

    const roleBasedSettings = Object.entries(settings).reduce((acc, [key, setting]) => {
        const { role, category, role_label } = setting;
        if (!acc[role_label]) {
            acc[role_label] = {};
        }
        if (!acc[role_label][category]) {
            acc[role_label][category] = [];
        }
        acc[role_label][category].push({ key, ...setting });
        return acc;
    }, {});
   
    // Sort roles to ensure operator is first if it exists
    // if there is other role beside entras_admin, then show only other role
    //let roleBasedSettings_ ={}
    const roles = Object.keys(roleBasedSettings).sort((a, b) => {
        if (a.toLowerCase() === 'operator') return -1;
        if (b.toLowerCase() === 'operator') return 1;
        return a.localeCompare(b);
    });

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const closeModal = () => {
        callSettingPair(originalSettings);
        onClose();
    }
    const callSettingPair = (orignalSettings) => {
        let pairs = { ...settingsPairs };
        Object.keys(orignalSettings).forEach(key => {
            if (key.includes("w55_manual_override")) {
                pairs.w55_manual_override.override = orignalSettings[key].value;
            }
            if (key.includes("w95_manual_override")) {
                pairs.w95_manual_override.override = orignalSettings[key].value;
            }
            if (key.includes("bo1_manual_override")) {
                pairs.bo1_manual_override.override = orignalSettings[key].value;
            }
            if (key.includes("w7_manual_override")) {
                pairs.w7_manual_override.override = orignalSettings[key].value;
            }
            if (key.includes("w55_manual_setpoint_value")) {
                pairs.w55_manual_override.setpoint_value = orignalSettings[key].value;
            }
            if (key.includes("w95_manual_setpoint_value")) {
                pairs.w95_manual_override.setpoint_value = orignalSettings[key].value;
            }
            if (key.includes("bo1_manual_setpoint_value")) {
                pairs.bo1_manual_override.setpoint_value = orignalSettings[key].value;
            }
            if (key.includes("w7_manual_setpoint_value")) {
                pairs.w7_manual_override.setpoint_value = orignalSettings[key].value;
            }
        });
        setSettingsPairs(pairs);
    }
    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await apiGet("UX_HTTP_API", `/esid-24-003/get-settings`);
            callSettingPair(response.settings);
            setSettings(response.settings);
            setOriginalSettings(response.settings);
        } catch (error) {
            console.error("Error fetching settings:", error);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchSettings();
    }, []);


    const handleChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value: value
            }
        }));
        //changeToggle(key, value);
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const simplifySettings = (detailedSettings) => {
                const simplifiedObj = Object.entries(detailedSettings).reduce((acc, [key, details]) => {
                    acc[key] = details.value;
                    return acc;
                }, {});
                Object.keys(simplifiedObj).forEach(key => {
                    if (key.includes('manual_override')) {
                        simplifiedObj[key] = settingsPairs[key].override;
                    }
                });

                return simplifiedObj;
            }

            await apiPost("UX_HTTP_API", "/esid-24-003/update-settings", { settings: simplifySettings(settings) });
            setOriginalSettings(settings);
            await fetchSettings();
            onClose();
            triggerAlert("success", "Settings saved successfully");

        } catch (error) {
            console.error("Error saving settings:", error);
            // return original settings
            triggerAlert("error", "Error saving settings");
            setSettings(originalSettings);
        } finally {
            setLoading(false);
        }
    };


    const isDisabled = (label) => {
        let parsedLabelArr = label.split("_");
        let parsedLabel = parsedLabelArr[0] + "_" + parsedLabelArr[1];
        let parsedLabelLong = parsedLabel + "_override";
        if (settingsPairs[parsedLabelLong]) {
            const setting = settingsPairs[parsedLabelLong];
            return !setting.override;
        } else {
            return false;
        }
    }
    const changeToggle = (label, value) => {
        let parsedLabelArr = label.split("_");
        let parsedLabel = parsedLabelArr[0] + "_" + parsedLabelArr[1];
        let keys = Object.keys(settingsPairs);
        if (keys.includes(label)) {
            setSettingsPairs(prev => ({
                ...prev,
                [label]: {
                    ...prev[label],
                    override: value
                }
            }));
        }
        else {
            setSettings(prev => ({
                ...prev,
                [label]: {
                    ...prev[label],
                    value: value
                }
            }));
        }
    }

    const findTrueToggle = (label) => {
        let keys = Object.keys(settingsPairs);
        if (keys.includes(label)) {
            return settingsPairs[label]?.override
        }
        else {
            return settings[label]?.value;
        }
    }

    return (
        <Modal open={open} onClose={closeModal}>
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                maxWidth: 900,
                maxHeight: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 4,
                zIndex: 998,
                borderRadius: 2,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '10px',
                    backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-track': {
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                },
                '&::-webkit-scrollbar-thumb': {
                    borderRadius: '8px',
                    backgroundColor: 'rgba(72,63,94,0.2)',
                    '&:hover': {
                        backgroundColor: 'rgba(72,63,94,0.4)',
                    },
                },
            }}>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center',
                }}>

                    <IconButton
                        onClick={closeModal}
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 1000,
                        }}
                    >
                        <CloseIcon fontSize="inherit"
                            sx={{ color: "black" }} />
                    </IconButton>
                </Box>

                <Tabs
                    value={currentTab}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: 2
                    }}
                >
                    {roles.map((role, index) => (
                        <Tab
                            key={role}
                            label={role.toUpperCase()}
                            sx={{
                                textTransform: 'capitalize',
                                fontWeight: 'medium'
                            }}
                        />
                    ))}
                </Tabs>
                {/* Settings content for current tab */}
                {roles.map((role, index) => (
                    <Box
                        key={role}
                        hidden={currentTab !== index}
                        sx={{ mt: 2 }}
                    >
                        {Object.entries(roleBasedSettings[role]).map(([category, items]) => (
                            <React.Fragment key={category}>
                                <Typography variant="h6" sx={{ mt: 2, mb: 2, ml: 1 }} component="h2" gutterBottom>
                                    {category}
                                </Typography>
                                <Grid container spacing={2}>
                                    {items.map(({ key, label, description, value }) => (
                                        typeof value === 'boolean' ? (
                                            <Grid item xs={3} key={key}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={findTrueToggle(key)}
                                                            onChange={(e) => changeToggle(key, e.target.checked)}
                                                        />
                                                    }
                                                    label={label}
                                                    title={description}
                                                    sx={{ my: 1, mr: 1, pt: 2 }}
                                                />
                                            </Grid>
                                        ) : (
                                            <Grid item xs={3} key={key}>
                                                <TextField
                                                    label={label}
                                                    value={value}
                                                    onChange={(e) => {
                                                        const newValue = e.target.type === 'number'
                                                            ? parseFloat(e.target.value)
                                                            : e.target.value;
                                                        handleChange(key, newValue);
                                                    }}
                                                    disabled={isDisabled(key)}
                                                    fullWidth
                                                    margin="normal"
                                                    helperText={description}
                                                    type={typeof value === 'number' ? 'number' : 'text'}
                                                    InputProps={{
                                                        step: 'any'
                                                    }}
                                                />
                                            </Grid>
                                        )
                                    ))}
                                </Grid>
                            </React.Fragment>
                        ))}
                    </Box>
                ))}
                {/* <Box sx={{ mt: 2 }}>
                    {Object.entries(categorizedSettings).map(([category, items]) => (
                        <React.Fragment key={category}>
                            <Typography variant="h6" sx={{ mt: 2, mb: 2, ml: 1 }} component="h2" gutterBottom>
                                {category}
                            </Typography>
                            <Grid container spacing={2}>
                                {items.map(({ key, label, description, value, role }) => {
                                    return typeof value === 'boolean' ? (
                                        <Grid item xs={3} key={key} >

                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Switch
                                                        checked={findTrueToggle(key)}
                                                        onChange={(e) => changeToggle(key, e.target.checked)}
                                                    />
                                                }
                                                label={label}
                                                title={description}
                                                sx={{ my: 1, mr: 1, pt: 2 }}
                                            />

                                        </Grid>
                                    ) : (
                                        <Grid item xs={3} key={key}>
                                            <TextField
                                                key={key}
                                                label={label}
                                                value={value}
                                                onChange={(e) => {

                                                    const newValue = e.target.type === 'number'
                                                        ? parseFloat(e.target.value)
                                                        : e.target.value;
                                                    handleChange(key, newValue);
                                                }}
                                                disabled={isDisabled(key)}
                                                fullWidth
                                                margin="normal"
                                                helperText={description}
                                                type={typeof value === 'number' ? 'number' : 'text'}
                                                InputProps={{
                                                    step: 'any' // Allows decimal numbers
                                                }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>

                        </React.Fragment>

                    ))}
                </Box> */}

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={closeModal} sx={{ width: '100px' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loadingApi}
                        sx={{ width: '100px', bgcolor: '#1F9562' }}
                    >
                        {loadingApi ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default SettingsModal;